import React, { ReactNode, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Container,
  Flex,
  HStack,
  Stack,
  StackSeparator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../App";
import { AccountView } from "../models/AccountView";
import { NavTab } from "../../shared/views/NavTab/NavTab";
import {
  AppHeader,
  AppHeaderHeight,
} from "../../shared/views/AppHeader/AppHeader";
import { useStores } from "../../shared/stores/RootStore";

interface Props {
  selectedView: AccountView;
  accountId: string;
  children?: ReactNode;
}
const AccountPageWrapperBase: React.FC<Props> = ({
  children,
  selectedView,
  accountId,
}) => {
  const { accountStore } = useStores();
  const navigate = useNavigate();
  const account = accountStore.account;

  useEffect(() => {
    accountStore.fetchAccount(accountId);
    // eslint-disable-next-line
  }, [accountId]);

  const renderTab = (to: string, view: AccountView) => (
    <NavTab
      onClick={() => navigate(to)}
      isSelected={selectedView === view}
      title={t(`account:${view}.tab`)}
    />
  );

  return (
    <Flex flexDir="column" maxHeight="100vh" flexGrow={1}>
      <AppHeader />
      <AppHeaderHeight />
      <Container
        flexGrow={1}
        display="flex"
        maxW="container.xl"
        overflow="hidden"
        px={0}
      >
        <HStack
          flexGrow={1}
          gap={0}
          separator={<StackSeparator />}
          alignItems="stretch"
        >
          <VStack
            flexShrink={0}
            width="300px"
            overflow="auto"
            alignItems="stretch"
            justifyContent="start"
            py={6}
            pl={2}
          >
            <Box>
              <Text px={2} fontSize="2xl" mb={1}>
                {account?.name}
              </Text>
              {renderTab(appRoutes.account(accountId), AccountView.Home)}
              {account?.permissions.canManageUsers &&
                renderTab(appRoutes.accountUsers(accountId), AccountView.Users)}
              {account?.permissions.canManageProjects &&
                renderTab(
                  appRoutes.accountProjects(accountId),
                  AccountView.Projects
                )}
              {account?.permissions.canManage &&
                renderTab(
                  appRoutes.accountIntegrations(accountId),
                  AccountView.Integrations
                )}
            </Box>
          </VStack>
          <Stack flexGrow={1} overflow="auto" py={6} position="relative">
            {children}
          </Stack>
        </HStack>
      </Container>
    </Flex>
  );
};

export const AccountPageWrapper = observer(AccountPageWrapperBase);
